// AUTH_SERVICE
import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { Injectable, signal } from '@angular/core';
import { Logger, LoggingService } from 'ionic-logging-service';
import { Observable, of, throwError } from 'rxjs';
import { catchError, first, switchMap, tap } from 'rxjs/operators';
import { EnvironmentService } from '../environment/environment.service';
import { PlatformService } from '../platform/platform.service';
import { StorageService } from '../storage/storage.service';
import { ITokens } from './models/tokens.model';
import { PkceService } from '../pkce/pkce.service';
import { AUTHORIZATION_QUERY_PARAMS, CLIENT_ID_BROWSER, CLIENT_ID_NATIVE, REFRESH_TOKENS_QUERY_PARAMS, REVOKE_TOKEN_QUERY_PARAMS } from './auth.constants';

export const NATIVE_SCHEME = 'printixapp://';

@Injectable({
  providedIn: 'root'
})
export class AuthService {

  private isCordova: boolean = this.platformService.isCordova();
  public isAuthenticatedSignal = signal(false);
  private logger: Logger;
  public  AuthAccessToken: string = 'AuthAccessToken';
  public  AuthRefreshToken: string = 'AuthRefreshToken';
  public  TokenTimeStamp: string = 'TokenTimeStamp';
  private TokenExpiresIn: string = 'AuthTokenTimeout'; // key-variable named after server naming(expires_in) for better readability. 'AuthTokenTimeout' string is used for persitence between ionic-release-app(this) and admin-app
  private authTokenKeys: string[] = [this.AuthAccessToken, this.AuthRefreshToken, this.TokenExpiresIn, this.TokenTimeStamp];
  // One-shot upgrade marker. The first time THIS build runs on a device we clear any
  // tokens left by a pre-PKCE build: those access/refresh tokens were issued to the
  // former client_id ('web_app' + client secret) and the new client_id can no longer
  // refresh them, so without this an upgraded user would be signed out mid-session on
  // the next refresh. Deliberately NOT in authTokenKeys — sign-out/sign-in must never
  // clear it, or the migration could re-fire and drop a freshly signed-in user's tokens.
  private AuthUpgradeTokenResetMarker: string = 'AuthUpgradeTokenReset_pkce_v1';

  constructor(
    private environmentService: EnvironmentService,
    private httpClient: HttpClient,
    private loggingService: LoggingService,
    private pkceService: PkceService,
    private platformService: PlatformService,
    private storageService: StorageService
  ) {
    this.logger = this.loggingService.getLogger("[AuthService]");
    const methodName = "ctor";
    this.logger.entry(methodName);
  }

  public removeStorageData(): Observable<void> {
    this.logger.info('removeStorageData()');
    return this.storageService.removeStoredItems(this.authTokenKeys).pipe(
      tap(() => {
        // Defense in depth: drop any lingering PKCE verifier so an abandoned sign-in
        // attempt doesn't leave a stale secret in sessionStorage across the next user's session.
        this.pkceService.clearCodeVerifier();
        this.isAuthenticatedSignal.set(false);
      })
    );
  }

  public setStorageData(tokens: ITokens): Observable<void> {
    this.logger.info('setStoreageData()');
    const access_token = tokens.access_token;
    const refresh_token = tokens.refresh_token;
    const expires_in = tokens.expires_in;
    
    return this.storageService.addItemToLocalStorage(this.AuthRefreshToken, refresh_token, 'setStorageData()').pipe(
      switchMap(() => {
        this.isAuthenticatedSignal.set(true);
        return this.storageService.addItemToLocalStorage(this.AuthAccessToken, access_token, 'setStorageData()');
      }),
      switchMap(() => this.storageService.addItemToLocalStorage(this.TokenTimeStamp, Date.now(), 'setStorageData()')),
      switchMap(() => this.storageService.addItemToLocalStorage(this.TokenExpiresIn, expires_in, 'setStorageData()'))
    );
  }

  public verifyUserCredentials(): Observable<void> {
    this.logger.info('verifyUserCredentials() get refreshToken from storage');
    // Run the one-shot upgrade token reset BEFORE reading the stored refresh token, so
    // an upgraded user's pre-PKCE tokens are cleared and they are routed to a clean
    // sign-in here rather than carrying a stale token into a later failing refresh.
    return this.runOneTimeTokenMigration().pipe(
      switchMap(() => this.storageService.getItemFromLocalStorage(this.AuthRefreshToken, 'verifyUserCredentials()')),
      switchMap((refreshToken: string) => {
        if (!refreshToken) {
          this.logger.info('verifyUserCredentials(): No stored refreshToken');
          return throwError(() => new Error('No stored refresh token'));
        }
        this.isAuthenticatedSignal.set(true);
        return of(void 0);
      })
    );
  }

  /**
   * One-shot, run-once-per-device token reset for users upgrading from a pre-PKCE
   * build. Those tokens were issued to the old client_id ('web_app' + client secret),
   * which the new client_id can no longer refresh, so we clear them on the first launch
   * of this build and record a persistent marker. Idempotent: no-ops on every later
   * launch. A fresh install just sets the marker (the clear is a harmless no-op).
   */
  private runOneTimeTokenMigration(): Observable<void> {
    const methodName = 'runOneTimeTokenMigration() ';
    return this.storageService.getItemFromLocalStorage(this.AuthUpgradeTokenResetMarker, methodName).pipe(
      switchMap((marker: string) => {
        if (marker) {
          return of(void 0);
        }
        this.logger.info(methodName + 'first launch of this build — clearing any pre-PKCE tokens');
        return this.removeStorageData().pipe(
          switchMap(() => this.storageService.addItemToLocalStorage(this.AuthUpgradeTokenResetMarker, 'done', methodName))
        );
      })
    );
  }

  public exchangeCodeForTokens(signInCode: string, tenantDomain: string, registrationCode?: string | null): Observable<void> {
    const methodName = 'exchangeCodeForTokens';
    this.logger.info(methodName);

    const ENDPOINT_AUTH_TOKENS = this.environmentService.getEnvironmentEndpoint['ENDPOINT_AUTH_TOKENS'];

    const redirectUri = new URL(this.isCordova
      ? (tenantDomain ? `${NATIVE_SCHEME}?${tenantDomain}` : NATIVE_SCHEME)
      : location.origin);
    // On web, the authorization request appends registrationCode to redirect_uri
    // (NetworkCheckPage.buildSignInUrl). RFC 6749 §4.1.3 requires the token-request
    // redirect_uri to be byte-identical to the one sent at authorization, so re-append
    // it here or a strict server rejects the GO-registration exchange with invalid_grant.
    // Native never adds it (its redirect is printixapp:?<tenant>), so this is web-only.
    if (!this.isCordova && registrationCode) {
      redirectUri.searchParams.set('registrationCode', registrationCode);
    }

    // URLSearchParams for POST body
    const searchParams = new URLSearchParams();
    AUTHORIZATION_QUERY_PARAMS.forEach(param => {
      const key = Object.keys(param)[0];
      let value = param[key];
      if (key === 'client_id') {value = this.isCordova ? CLIENT_ID_NATIVE : CLIENT_ID_BROWSER;}
      if (key === 'redirect_uri') {value = redirectUri.toString();}
      if (key === 'code') {value = signInCode;}
      searchParams.set(key, value);
    });

    // PKCE code_verifier is required by the token endpoint — bail early with a clear
    // error if it is missing (e.g. sessionStorage cleared between redirect and callback).
    const codeVerifier = this.pkceService.getAndClearCodeVerifier();
    if (!codeVerifier) {
      const errorMessage = 'missing PKCE code_verifier; restart the sign-in flow before exchanging the authorization code.';
      this.logger.error(methodName, errorMessage);
      return throwError(() => new Error(errorMessage));
    }
    searchParams.set('code_verifier', codeVerifier);

    this.logger.info('## AuthService exchangeCodeForTokens() preparing token request');
    const authParams = searchParams.toString();
    const headers = new HttpHeaders({
      'Content-Type': 'application/x-www-form-urlencoded'
    });
    // No retry: the authorization code (and PKCE verifier) is single-use. A retry
    // after a lost response would resend a code the server already consumed and get
    // a hard invalid_grant. On failure the user simply restarts the sign-in flow.
    return this.httpClient.post<ITokens>(ENDPOINT_AUTH_TOKENS, authParams, { headers }).pipe(
      switchMap((tokens: ITokens) => this.setStorageData(tokens)),
      catchError((error: HttpErrorResponse) => {
        this.logger.error('exchangeCodeForTokens() error:', error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Consolidated method for getting a valid token for HTTP requests
   * Handles all auth logic internally: validation, refresh, storage operations
   * Returns clean token or throws clean error
   */
  public getValidTokenForRequest(url?: string): Observable<string> {
    this.logger.info('getValidTokenForRequest() for url:', url);

    const now = Date.now();

    // Get all required data in parallel to avoid nested switchMaps
    return this.storageService.getItemFromLocalStorage(this.AuthAccessToken, 'getValidTokenForRequest').pipe(
      switchMap((accessToken: string) => {
        if (!accessToken) {
          this.logger.info('No access token found - refreshing');
          return this.performTokenRefresh();
        }

        // Check if token is still valid
        return this.storageService.getItemFromLocalStorage(this.TokenExpiresIn, 'getValidTokenForRequest').pipe(
          switchMap((tokenExpiresIn: number) => {
            if (!tokenExpiresIn) {
              this.logger.info('No token expiry found - refreshing');
              return this.performTokenRefresh();
            }

            return this.storageService.getItemFromLocalStorage(this.TokenTimeStamp, 'getValidTokenForRequest').pipe(
              switchMap((tokenTimeStamp: number) => {
                if (!tokenTimeStamp) {
                  this.logger.info('No token timestamp found - refreshing');
                  return this.performTokenRefresh();
                }

                const tokenTimeOutAdjusted = tokenExpiresIn - 20;
                const isTokenValid = (tokenTimeStamp + tokenTimeOutAdjusted * 1000) > now;

                if (isTokenValid) {
                  this.logger.info('Token is valid, returning existing token');
                  return of(accessToken);
                } else {
                  this.logger.info('Token expired - refreshing');
                  return this.performTokenRefresh();
                }
              })
            );
          })
        );
      }),
      catchError((error) => {
        this.logger.error('getValidTokenForRequest() error:', error);
        throw error; // Clean error propagation
      })
    );
  }

  /**
   * Retrieves the stored refresh token from local storage
   * Can be included in performTokenRefresh again, once the refreshToken parameter is no longer needed
   */
  private getStoredRefreshToken(): Observable<string> {
    return this.storageService.getItemFromLocalStorage(this.AuthRefreshToken, 'getStoredRefreshToken');
  }

  /**
   * Consolidated method to handle token refresh logic
   * Public so it can be used by AuthInterceptor
   */
  public performTokenRefresh(newRefreshToken?: string): Observable<string> {
    this.logger.info('performTokenRefresh()');

    const refreshToken$ = newRefreshToken 
      ? of(newRefreshToken)
      : this.getStoredRefreshToken();

    return refreshToken$.pipe(
      switchMap((refreshToken: string) => {
        if (!refreshToken) {
          throw new Error('No refresh token available');
        }

        const ENDPOINT_AUTH_TOKENS = this.environmentService.getEnvironmentEndpoint['ENDPOINT_AUTH_TOKENS'];
        const headers = new HttpHeaders({
          'Content-Type': 'application/x-www-form-urlencoded'
        });
  
        // URLSearchParams for POST body
        const searchParams = new URLSearchParams();
        REFRESH_TOKENS_QUERY_PARAMS.forEach(param => {
          const key = Object.keys(param)[0];
          let value = param[key];
          if (key === 'client_id') {value = this.isCordova ? CLIENT_ID_NATIVE : CLIENT_ID_BROWSER;}
          if (key === 'refresh_token') {value = refreshToken;}
          searchParams.set(key, value);
        });

        const authParams = searchParams.toString();

        return this.httpClient.post<ITokens>(ENDPOINT_AUTH_TOKENS, authParams, { headers }).pipe(
          first(),
          switchMap((tokens: ITokens) => {
            this.isAuthenticatedSignal.set(true);
            return this.setStorageData(tokens).pipe(
              switchMap(() => of(tokens.access_token))
            );
          })
          // No catchError here - let errors propagate to calling component
          // The calling component's error handling (like handleUserAuthentication) will handle 401/403
        );
      })
    );
  }

  public deAuthenticateUser(): Observable<void> {
    this.logger.info('deAuthenticateUser()');

   return this.storageService.getItemFromLocalStorage(this.AuthRefreshToken, 'deAuthenticateUser()').pipe(
      switchMap((refreshToken: string) => {
        if (refreshToken) {
          const ENDPOINT_REVOKE_TOKEN = this.environmentService.getEnvironmentEndpoint['ENDPOINT_REVOKE_TOKEN'];
          const headers = new HttpHeaders({
            'Content-Type': 'application/x-www-form-urlencoded'
          });
          // The revoke endpoint takes the token to revoke and the client_id as form body parameters.
          const body = new URLSearchParams();
          REVOKE_TOKEN_QUERY_PARAMS.forEach(param => {
            const key = Object.keys(param)[0];
            let value = param[key];
            if (key === 'client_id') {value = this.isCordova ? CLIENT_ID_NATIVE : CLIENT_ID_BROWSER;}
            if (key === 'token') {value = refreshToken;}
            body.set(key, value);
          });
          // withCredentials lets the browser attach the SESSION cookie set by
          // the auth server during sign-in (cross-subdomain: app on tenant-N.*,
          // auth on auth.*). The cookie itself is HttpOnly and is managed by
          // the browser, not by this code.
          return this.httpClient.post<void>(ENDPOINT_REVOKE_TOKEN, body.toString(), { headers, withCredentials: true }).pipe(
            switchMap(() => this.removeStorageData()),
            catchError((error: HttpErrorResponse) => {
              this.logger.error('deAuthenticateUser() http error:', error);
              return this.removeStorageData().pipe(
                switchMap(() => throwError(() => error))
              );
            })
          );
        } else {
          return this.removeStorageData();
        }
      }),
      catchError((error) => {
        this.logger.error('deAuthenticateUser() error:', error);
        return throwError(() => error);
      })
    );
  }
}